<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { DxDropDownBox, DxList } from 'devextreme-vue'
import { DxButton } from 'devextreme-vue/button'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNotify } from '@/composables/notify'

const props = defineProps({
  gridExtRef: Object,
  gridExtId: String,
})

const webSiteStore = useWebSiteStore()
const i18n = getI18n()
const { notifyError } = useNotify()

const isDropDownBoxOpened = ref(false)
const inputValue = ref('')
const activeLayoutId = ref(null)
const lastSavedLayoutState = ref(null)
const isApplyingLayout = ref(false)
const dropDownBoxRef = ref(null)
const isLayoutModified = ref(false)
const isDefaultModified = ref(false)
const inputError = ref(false)

const defaultLayout = computed(() => ({
  id: 'default-grid-layout',
  name: i18n.t('common.default'),
  is_default: true,
  is_active: false,
  grid_id: props.gridExtId || null,
  layout: null,
}))

const availableLayouts = computed(() => {
  const gridId = props.gridExtId || null
  const savedLayouts = (webSiteStore.gridLayoutConfigurations || []).filter(layout => (layout.grid_id || null) === gridId)
  return [defaultLayout.value, ...savedLayouts]
})

const currentActiveLayout = computed(() => availableLayouts.value.find(layout => layout.is_active) || defaultLayout.value)

const displayedLayoutName = computed(() => currentActiveLayout.value?.name || '')

const selectedLayoutIds = computed(() => activeLayoutId.value ? [activeLayoutId.value] : [])

const isSaveButtonEnabled = computed(() => inputValue.value.trim().length > 0 || isLayoutModified.value || isDefaultModified.value)

const canDeleteLayout = computed(() => currentActiveLayout.value && !currentActiveLayout.value.is_default)

const getGridInstance = () => props.gridExtRef?.instance

const getGridState = () => {
  const grid = getGridInstance()
  if (!grid?.state) return null
  return grid.state()
}

const getStructuralStateSignature = () => {
  const state = getGridState()
  return state ? JSON.stringify(state) : null
}

const isStructuralChange = (optionName) =>
  typeof optionName === 'string' && /^(columns\[|grouping|sorting|filtering)/.test(optionName)

const applyLayoutToGrid = async(layoutState) => {
  const grid = getGridInstance()
  if (!grid?.state) return

  isApplyingLayout.value = true
  grid.state(layoutState)
  lastSavedLayoutState.value = getStructuralStateSignature()
  isApplyingLayout.value = false
}

const resetGridToDefault = async() => {
  const grid = getGridInstance()
  if (!grid) return

  isApplyingLayout.value = true
  grid.state(null)
  grid.option('focusedRowIndex', 0)
  isApplyingLayout.value = false
}

const loadLayoutConfigurations = async() => {
  try {
    await webSiteStore.getOrFetchGridLayoutConfigurationsAsync()
    syncActiveLayoutFromStore()
  } catch (error) {
    notifyError(i18n.t('error.loadingConfigurations'))
  }
}

const syncActiveLayoutFromStore = (force = false) => {
  const active = currentActiveLayout.value
  activeLayoutId.value = active.id
  lastSavedLayoutState.value = getStructuralStateSignature()
  isLayoutModified.value = false
  isDefaultModified.value = false
  inputError.value = false

  if (force && (active?.layout || active?.is_default)) {
    applyLayoutToGrid(JSON.parse(active.layout))
  }
}

const createNewLayout = async() => {
  const layoutName = inputValue.value.trim()
  if (!layoutName || layoutName === defaultLayout.value.name) return

  const layoutJson = getGridState() ? JSON.stringify(getGridState()) : null

  try {
    await webSiteStore.api.createGridLayoutConfigurationAsync({
      name: layoutName,
      grid_id: props.gridExtId || null,
      layout: layoutJson,
      is_active: true,
    })

    inputValue.value = ''
    await loadLayoutConfigurations()
    isDropDownBoxOpened.value = false
  } catch {
    notifyError(i18n.t('error.duplicateLayout'))
  }
}

const saveCurrentLayout = async() => {
  if (isLayoutModified.value && !currentActiveLayout.value?.is_default) {
    const layoutJson = getGridState() ? JSON.stringify(getGridState()) : null
    try {
      await webSiteStore.api.updateGridLayoutConfigurationAsync(
        currentActiveLayout.value.id,
        { layout: layoutJson }
      )
      await loadLayoutConfigurations()
    } catch {
      notifyError(i18n.t('error.saving'))
    }
  }
}

const onSaveActiveLayout = async() => {
  const hasInput = inputValue.value.trim().length > 0

  if (hasInput) {
    await createNewLayout()
  } else if (isDefaultModified.value) {
    inputError.value = true
  } else if (isLayoutModified.value) {
    await saveCurrentLayout()
  }
}

const onDeleteActiveLayout = async() => {
  const layoutToDelete = currentActiveLayout.value
  if (!layoutToDelete || layoutToDelete.is_default) return

  const layouts = availableLayouts.value
  const currentIndex = layouts.findIndex(l => l.id === layoutToDelete.id)
  const nextLayout = layouts[currentIndex + 1] ?? layouts[0]

  try {
    await webSiteStore.api.deleteGridLayoutConfigurationAsync(layoutToDelete.id)
  } catch {
    notifyError(i18n.t('error.deleting'))
    return
  }

  if (nextLayout.is_default) {
    await resetGridToDefault()
    await webSiteStore.api.deactivateGridLayoutConfigurationsAsync({ grid_id: props.gridExtId })
  } else {
    try {
      await webSiteStore.api.updateGridLayoutConfigurationAsync(nextLayout.id, { is_active: true })
      if (nextLayout.layout) { await applyLayoutToGrid(JSON.parse(nextLayout.layout)) }
    } catch {
      notifyError(i18n.t('error.saving'))
    }
  }

  await loadLayoutConfigurations()
}

const onLayoutSelected = async(selectionEvent) => {
  if (!isDropDownBoxOpened.value) return

  const selectedLayout = selectionEvent.addedItems?.[0]
  if (!selectedLayout) return

  activeLayoutId.value = selectedLayout.id

  if (selectedLayout.is_default) {
    await resetGridToDefault()
    try {
      await webSiteStore.api.deactivateGridLayoutConfigurationsAsync({ grid_id: props.gridExtId })
      await loadLayoutConfigurations()
    } catch {
      notifyError(i18n.t('error.saving'))
    }
    isDropDownBoxOpened.value = false
    return
  }

  if (selectedLayout.layout && getGridInstance()) {
    try {
      const layoutState = JSON.parse(selectedLayout.layout)
      await applyLayoutToGrid(layoutState)
    } catch {
      notifyError(i18n.t('error.saving'))
      return
    }
  }

  if (!selectedLayout.is_active) {
    try {
      await webSiteStore.api.updateGridLayoutConfigurationAsync(selectedLayout.id, { is_active: true })
    } catch {
      notifyError(i18n.t('error.saving'))
      return
    }
  }

  await loadLayoutConfigurations()
  isDropDownBoxOpened.value = false
}

const onGridOptionChanged = async(event) => {
  if (isApplyingLayout.value || !isStructuralChange(event?.fullName)) return

  await nextTick()

  const currentSignature = getStructuralStateSignature()
  if (currentSignature === lastSavedLayoutState.value) return

  if (currentActiveLayout.value?.is_default) {
    isDefaultModified.value = true
  } else {
    isLayoutModified.value = true
  }
}

const onDropDownOpened = async() => {
  await nextTick()
  dropDownBoxRef.value?.instance?.focus()
}

watch(isDropDownBoxOpened, (isOpened) => {
  if (!isOpened) {
    inputValue.value = ''
    inputError.value = false
  }
})

watch(inputValue, (val) => {
  if (val && inputError.value) { inputError.value = false }
})

onMounted(async() => {
  const grid = getGridInstance()
  if (grid?.on) {
    grid.on('optionChanged', onGridOptionChanged)
  }

  setTimeout(() => {
    const isFirstMount = grid && !grid._layoutInitialized
    if (grid) grid._layoutInitialized = true
    syncActiveLayoutFromStore(isFirstMount)
  }, 0)
})

onUnmounted(() => {
  const grid = getGridInstance()
  if (grid?.off) {
    grid.off('optionChanged', onGridOptionChanged)
  }
})
</script>

<template>
  <div class="grid-layout-wrapper">
    <div class="grid-layout-config">
      <dx-drop-down-box
        ref="dropDownBoxRef"
        v-model:value="inputValue"
        v-model:opened="isDropDownBoxOpened"
        :is-valid="!inputError"
        :open-on-field-click="true"
        :accept-custom-value="true"
        :search-enabled="true"
        :placeholder="displayedLayoutName || i18n.t('common.selectLayout')"
        :drop-down-options="{ wrapperAttr: { class: 'drop-down-box-config-popup' } }"
        @opened="onDropDownOpened"
        class="drop-down-box-config"
      >
        <template #contentTemplate>
          <dx-list
            :data-source="availableLayouts"
            :selected-item-keys="selectedLayoutIds"
            key-expr="id"
            selection-mode="single"
            display-expr="name"
            @selection-changed="onLayoutSelected"
          />
        </template>
      </dx-drop-down-box>

      <dx-button
        :disabled="!isSaveButtonEnabled"
        :element-attr="{ class: isSaveButtonEnabled ? 'layout-save-highlight' : '' }"
        icon="save"
        :hint="i18n.t('common.save')"
        styling-mode="text"
        @click="onSaveActiveLayout"
      />

      <dx-button
        :disabled="!canDeleteLayout"
        icon="trash"
        :hint="i18n.t('common.delete')"
        styling-mode="text"
        @click="onDeleteActiveLayout"
      />
    </div>
    <q-separator vertical inset />
  </div>
</template>

<style scoped lang="scss">
.grid-layout-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.grid-layout-config {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.layout-save-highlight {
  :deep(.dx-icon) {
    color: darkorange;
  }
}

:global(.drop-down-box-config-popup .dx-popup-content) {
  padding: 0 !important;
}
</style>
